import Head from "next/head";
import { buildAbsoluteInternalLink, setTrailingSlash } from "../../utils/url";
import { ImageType } from "@/types/baseTypes";
import { getHeader } from "@/wp/getHeader";
import { OptionsSecurityHeader } from "@/types/OptionType";
import { Post } from "@/wp/types/client";

interface HeadMetaProps {
  post: Post;
  fallbackShareImage: ImageType | false;
  securityHeaderOptions: OptionsSecurityHeader;
}

// TODO do not pass full options object to module only pass the props needed to render the component!!!!
export const HeadMeta = ({
  post,
  fallbackShareImage,
  securityHeaderOptions,
}: HeadMetaProps) => {
  const postHeader = getHeader({ post });
  const structuredData =
    postHeader && "structured_data" in postHeader
      ? postHeader.structured_data
      : undefined;
  const yoast = post?.yoast_head_json;
  const wpml_current_locale = post?.wpml_current_locale || "de_DE";

  const fallback = fallbackShareImage
    ? {
        url: fallbackShareImage.url,
        width: fallbackShareImage.width,
        height: fallbackShareImage.height,
      }
    : false;

  const OG_IMAGE = yoast?.og_image?.[0] || fallback;

  const scriptURLs =
    securityHeaderOptions?.content_security_policy_header?.script_urls;
  const imgURLs =
    securityHeaderOptions?.content_security_policy_header?.image_urls;
  const connectURLs =
    securityHeaderOptions?.content_security_policy_header?.connect_urls;
  const iframeURLs =
    securityHeaderOptions?.content_security_policy_header?.iframe_urls;
  const mediaURLs =
    securityHeaderOptions?.content_security_policy_header?.media_urls;
  const fontURLs =
    securityHeaderOptions?.content_security_policy_header?.font_urls;
  const styleURLs =
    securityHeaderOptions?.content_security_policy_header?.style_urls;
  const cspContent = `
  script-src 'self' 'unsafe-inline' 'unsafe-eval' ${scriptURLs ? scriptURLs.join(" ") : ""};
  style-src 'self' 'unsafe-inline' ${styleURLs ? styleURLs.join(" ") : ""};
  img-src 'self' ${process.env.WP_API_URL} ${imgURLs ? imgURLs.join(" ") : ""} data:;
  font-src 'self' ${fontURLs ? fontURLs.join(" ") : ""} data:; 
  connect-src 'self' ${process.env.WP_API_URL} ${connectURLs ? connectURLs.join(" ") : ""};
  frame-src 'self' ${iframeURLs ? iframeURLs.join(" ") : ""};
  media-src 'self' ${mediaURLs ? mediaURLs.join(" ") : ""};
`
    .replace(/\s{2,}/g, " ")
    .trim(); // Minify CSP

  return (
    <Head>
      <link rel="dns-prefetch" href={process.env.WP_API_URL} />
      <link rel="preconnect" href={process.env.WP_API_URL} />
      <link
        rel="preconnect"
        href={process.env.WP_API_URL}
        crossOrigin="anonymous"
      />
      <link
        rel="icon"
        type="image/png"
        href="/favicon-96x96.png"
        sizes="96x96"
      />
      <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
      <link rel="shortcut icon" href="/favicon.ico" />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-touch-icon.png"
      />
      <link
        rel="manifest"
        href="/site.webmanifest"
        crossOrigin="use-credentials"
      />
      {/* Title */}
      <title>{yoast?.title}</title>
      {/*
      // ! Add this in the consent management backend plugin
      <script
        async
        src="https://platform.twitter.com/widgets.js"
        charSet="utf-8"
      ></script> */}

      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="description" content={yoast?.description} />
      {/* Meta Name Robots */}
      {yoast?.robots && (
        <meta
          name="robots"
          content={[
            yoast?.robots.index,
            yoast?.robots.follow,
            yoast?.robots["max-image-preview"],
            yoast?.robots["max-snippet"],
            yoast?.robots["max-video-preview"],
          ].toString()}
        />
      )}
      {OG_IMAGE && (
        <>
          <meta property="og:image" content={OG_IMAGE.url} />
          <meta property="og:image:width" content={OG_IMAGE.width.toString()} />
          <meta
            property="og:image:height"
            content={OG_IMAGE.height.toString()}
          />
        </>
      )}
      {/* Meta Property OG LOCALE CONTENT */}
      <meta property="og:locale" content={yoast?.og_locale} />
      {/* Meta Property OG TYPE*/}
      <meta property="og:type" content={yoast?.og_type} />
      {/* Meta Property OG TITLE */}
      <meta property="og:title" content={yoast?.og_title} />
      {/* Meta Property OG DESCRIPTION */}
      <meta property="og:description" content={yoast?.og_description} />
      {/* Meta Property OG URL */}
      <meta property="og:url" content={setTrailingSlash(yoast?.og_url)} />
      {/* Meta Property OG SITE_NAME */}
      <meta property="og:site_name" content={yoast?.og_site_name} />
      {/* Meta Property ARTICLE:MODIFIED_TIME  */}
      <meta
        property="article:modified_time"
        content={yoast?.article_modified_time}
      />
      {yoast?.canonical && (
        <link
          rel="canonical"
          href={buildAbsoluteInternalLink(yoast.canonical)}
        />
      )}
      {/* Meta Name */}
      <meta name="twitter_card" content={yoast?.twitter_card} />

      {/* Current language */}
      {post.link && (
        <link
          rel="alternate"
          //if wpml is not setup (single language project) fallback to first of NEXT_PUBLIC_LOCALES
          //if NEXT_PUBLIC_LOCALES env is not set fallback to "de"
          //TODO: for SINGLE LANGUAGE project which is NOT GERMAN: set NEXT_PUBLIC_LOCALES or change the fallback language from "de" to the actual language
          hrefLang={
            wpml_current_locale.split("_")[0] ??
            (process.env.NEXT_PUBLIC_LOCALES !== ""
              ? process.env.NEXT_PUBLIC_LOCALES?.split(",")[0]
              : undefined) ??
            "de"
          }
          href={buildAbsoluteInternalLink(post.link)}
        />
      )}

      {/* Other available languages */}
      {post.wpml_translations?.map((translation, key) => {
        return (
          translation.href &&
          translation.locale && (
            <link
              key={key}
              rel="alternate"
              hrefLang={translation.locale.split("_")[0]}
              href={buildAbsoluteInternalLink(translation.href)}
            />
          )
        );
      })}
      {((structuredData && structuredData.length > 0) ||
        (typeof yoast?.schema === "object" && yoast?.schema !== null)) && (
        <script type="application/ld+json">
          {structuredData && structuredData.length > 0
            ? structuredData
            : JSON.stringify(yoast.schema)}
        </script>
      )}
      {securityHeaderOptions?.enable_content_security_policy && (
        <meta http-equiv="Content-Security-Policy" content={cspContent} />
      )}
      {securityHeaderOptions?.referrer_policy && (
        <meta name="referrer" content="no-referrer" />
      )}
    </Head>
  );
};
